import { Users, Home, Heart, Eye, Trees, MonitorX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export const AboutSection = () => {
  const navigate = useNavigate();

  const handleLearnMore = () => {
    navigate('/about-mystic-forest');
  };

  return (
    <section id="about" className="py-24 bg-card relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full border border-accent animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 rounded-full border border-accent animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 rounded-full border border-accent animate-pulse delay-500"></div>
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-serif font-light mb-8 text-foreground leading-tight">
            Our Roots
          </h2>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-accent to-transparent mx-auto mb-6"></div>
        </div>

        <div className="space-y-16">
          {/* Main description with enhanced styling */}
          <div className="text-center max-w-4xl mx-auto">
            <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-border/50 shadow-lg">
              {/* Main headline */}
              <h3 className="text-3xl md:text-4xl leading-tight text-foreground mb-8 font-serif font-light">
                <span className="text-accent font-medium">Mystic Forest</span> is a home for curated offline experiences
              </h3>
              
              {/* Core Values - Better organized */}
              <div className="space-y-6 text-base md:text-lg leading-relaxed">
                <p className="text-foreground/90 text-xl">
                  We create analogue retreats, events, and workshops to help you:
                </p>
                
                <ul className="space-y-4 max-w-2xl mx-auto inline-block text-left">
                  <li className="flex items-start gap-3">
                    <span className="text-accent text-xl mt-0.5 flex-shrink-0">•</span>
                    <span className="text-foreground/90"><strong className="text-foreground">Slow down</strong> from the everyday rush</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-accent text-xl mt-0.5 flex-shrink-0">•</span>
                    <span className="text-foreground/90"><strong className="text-foreground">Recharge</strong> your body and mind</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-accent text-xl mt-0.5 flex-shrink-0">•</span>
                    <span className="text-foreground/90"><strong className="text-foreground">Reconnect</strong> with what matters most</span>
                  </li>
                </ul>
                
                <div className="w-16 h-px bg-accent/30 mx-auto my-8"></div>
                
                <p className="text-foreground/90 text-xl">
                  <span className="relative inline-block">
                    <span className="absolute inset-0 bg-lime-400 opacity-60 transform -rotate-1 rounded-sm -z-10"></span>
                    <span className="relative px-2 font-medium text-foreground">An antidote to digital overload</span>
                  </span>
                </p>
              </div>
            </div>
          </div>
          
          {/* Two Rhythms Section */}
          <div className="space-y-12">
            <div className="text-center">
              <h3 className="text-3xl md:text-4xl font-serif font-light text-foreground mb-4">
                Two Rhythms. One Intention.
              </h3>
              <div className="w-20 h-px bg-accent mx-auto"></div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              <div className="group hover-scale h-full">
                <div className="bg-background/80 backdrop-blur-sm p-8 md:p-10 rounded-2xl border-2 border-accent/10 shadow-lg hover:shadow-xl hover:border-accent/20 transition-all duration-300 h-full flex flex-col">
                  <div className="w-16 h-16 bg-gradient-to-br from-accent/20 to-accent/10 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300">
                    <Home className="w-8 h-8 text-accent" />
                  </div>
                  <h4 className="text-2xl font-serif font-medium text-foreground mb-6 text-center">
                    Everyday Pause
                  </h4>
                  <p className="text-muted-foreground leading-relaxed text-center flex-1">
                    Short, city-based workshops. Close to home – yet a break from the noise. 
                    A moment to slow down, breathe, and reconnect in the middle of daily life.
                  </p>
                </div>
              </div>
              
              <div className="group hover-scale h-full">
                <div className="bg-background/80 backdrop-blur-sm p-8 md:p-10 rounded-2xl border-2 border-accent/10 shadow-lg hover:shadow-xl hover:border-accent/20 transition-all duration-300 h-full flex flex-col">
                  <div className="w-16 h-16 bg-gradient-to-br from-accent/20 to-accent/10 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300">
                    <Trees className="w-8 h-8 text-accent" />
                  </div>
                  <h4 className="text-2xl font-serif font-medium text-foreground mb-6 text-center">
                    Deep Reset
                  </h4>
                  <p className="text-muted-foreground leading-relaxed text-center flex-1">
                    Weekend retreats out in nature. Far from screens, closer to yourself. 
                    Time stretches, the signal fades – and real presence begins.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional content with better styling */}
          <div className="bg-background/40 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-border/10">
            <div className="max-w-3xl mx-auto">
              <h4 className="text-2xl font-serif font-light text-center mb-6 text-foreground">
                What Makes Us Different
              </h4>
              <ul className="space-y-4 text-lg max-w-2xl mx-auto inline-block text-left">
                <li className="flex items-start gap-3">
                  <span className="text-accent text-xl mt-1 flex-shrink-0">✓</span>
                  <span className="text-muted-foreground">
                    <strong className="text-foreground">Expert-Led:</strong> Guided by therapists, yoga instructors, mindfulness coaches, and circle leaders
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-accent text-xl mt-1 flex-shrink-0">✓</span>
                  <span className="text-muted-foreground">
                    <strong className="text-foreground">Holistic Care:</strong> Focus on body, emotions, connections, and mental balance
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-accent text-xl mt-1 flex-shrink-0">✓</span>
                  <span className="text-muted-foreground">
                    <strong className="text-foreground">Magical Venues:</strong> Set in Poland's most inspiring natural locations
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-accent text-xl mt-1 flex-shrink-0">✓</span>
                  <span className="text-muted-foreground">
                    <strong className="text-foreground">Authentically Grounded:</strong> We honour depth and presence — without drifting into esoteric clichés
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Learn More Button */}
          <div className="text-center mt-8">
            <Button 
              onClick={handleLearnMore}
              className="px-12 py-4 text-lg font-medium hover-scale bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              Step Deeper into the Forest
            </Button>
          </div>

        </div>
      </div>
    </section>
  );
};